import { BehaviorSubject, Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';

import { UiHelpersService } from '../../services';

export interface Coord {
  x: number;
  y: number;
}

export interface ImgSize {
  width: number;
  height: number;
}

@Component({
  selector: 'phoenix-simple-zoom',
  templateUrl: './simple-zoom.component.html',
  styleUrls: ['./simple-zoom.component.scss'],
  standalone: false,
})
export class SimpleZoomComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('zoomParentContainer', { static: true })
  zoomParentContainer!: ElementRef;
  @ViewChild('zoomContainer', { static: true }) zoomContainer!: ElementRef;
  @ViewChild('imageThumbnail', { static: true }) imageThumbnail!: ElementRef;
  @ViewChild('fullSizeImage', { static: true }) fullSizeImage!: ElementRef;

  changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  // Use ImgSize interface for dimension tracking
  private containerSize: ImgSize = { width: 0, height: 0 };
  private originalImageSize: ImgSize = { width: 0, height: 0 };
  private dimensionCheckInterval: any = null;
  private readonly CHECK_INTERVAL = 500; // Check every 500ms

  @Output() imagesLoaded = new EventEmitter<boolean>();
  @Output() zoomPositionChange = new EventEmitter<Coord>();

  _imgUrl = '';
  @Input() set imgUrl(value: string) {
    if (value !== this._imgUrl) {
      this.fullImageLoaded = false;
      this.zoomOff();
      this.checkImage(value);
    }
  }
  @Input() altText = '';
  @Input() titleText = '';

  @Input() enableLens = false;
  @Input() lensWidth = 100;
  @Input() lensHeight = 100;
  @Input() magnification = 1;
  @Input() maxZoomRatio = 2;

  public zoomDisplay = 'none';
  public thumbWidth = 0;
  public thumbHeight = 0;
  public fullImageTop = 0;
  public fullImageLeft = 0;
  public lensTop = 0;
  public lensLeft = 0;
  public magnifiedWidth = 0;
  public magnifiedHeight = 0;
  public zoomPosition = new BehaviorSubject<Coord | null>(null);

  public zoomingEnabled = false;
  public isReady = false;
  public baseRatio?: number;
  public minZoomRatio = 1;

  public fullImageLoaded: boolean = false;

  public fullWidth = 0;
  public fullHeight = 0;
  private xRatio = 0;
  private yRatio = 0;
  private latestMouseLeft = -1;
  private latestMouseTop = -1;
  private thumbImageLoaded = false;
  private zoomFrozen = false;

  uiHelpers = inject(UiHelpersService);

  constructor() {
    this.zoomPosition.pipe(takeUntil(this.destroy$)).subscribe(position => {
      if (position) this.zoomPositionChange.emit(position);
    });
  }

  ngOnInit(): void {
    this.startDimensionChecking();
  }

  ngOnChanges(): void {
    this.calculateRatioAndOffset();
    this.calculateImageAndLensPosition();
  }

  ngOnDestroy(): void {
    this.stopDimensionChecking();

    this.destroy$.next();
    this.destroy$.complete();
  }

  private startDimensionChecking(): void {
    this.stopDimensionChecking();

    this.dimensionCheckInterval = interval(this.CHECK_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkContainerDimensions());
  }

  private stopDimensionChecking(): void {
    if (this.dimensionCheckInterval) {
      this.dimensionCheckInterval.unsubscribe();
      this.dimensionCheckInterval = null;
    }
  }

  getContainedSize(img: ImgSize, container: ImgSize): ImgSize {
    if (container.width === 0 || container.height === 0) return img;

    const ratio = img.width / img.height;
    let width = container.height * ratio;
    let height = container.height;

    if (width > container.width) {
      width = container.width;
      height = container.width / ratio;
    }

    return { width, height };
  }

  onThumbImageLoaded(): void {
    this.thumbImageLoaded = true;
    this.checkImagesLoaded();

    // Check dimensions immediately after the thumbnail is loaded
    this.checkContainerDimensions();
  }

  private checkContainerDimensions(): void {
    if (!this.zoomParentContainer || !this._imgUrl) return;

    if (
      this.originalImageSize.width === 0 ||
      this.originalImageSize.height === 0
    )
      return;

    const container = this.zoomParentContainer.nativeElement;
    const currentWidth = container.clientWidth;
    const currentHeight = container.clientHeight;

    if (currentWidth === 0 || currentHeight === 0) return;

    if (
      currentWidth !== this.containerSize.width ||
      currentHeight !== this.containerSize.height
    ) {
      this.zoomOff();
      this.containerSize = { width: currentWidth, height: currentHeight };

      this.recalculateImageDimensions();
    }
  }

  private recalculateImageDimensions(): void {
    if (
      this.originalImageSize.width === 0 ||
      this.originalImageSize.height === 0 ||
      !this._imgUrl
    )
      return;

    const imgSize = this.getContainedSize(
      this.originalImageSize,
      this.containerSize
    );

    this.thumbWidth = imgSize.width;
    this.thumbHeight = imgSize.height;

    this.calculateRatioAndOffset();
    if (this.fullImageLoaded) {
      this.calculateImageAndLensPosition();
    }

    this.markForCheck();
  }

  private checkImage(imageSrc: string): void {
    if (!imageSrc) return;

    const img = new Image();
    img.src = imageSrc;

    img.onload = () => {
      const container = this.zoomParentContainer.nativeElement;

      this.containerSize = {
        width: container.clientWidth,
        height: container.clientHeight,
      };

      this.originalImageSize = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };

      const imgSize = this.getContainedSize(
        this.originalImageSize,
        this.containerSize
      );

      this.thumbWidth = imgSize.width;
      this.thumbHeight = imgSize.height;
      this._imgUrl = imageSrc;
      this.markForCheck();
    };

    img.onerror = () => {
      if (this._imgUrl !== this.uiHelpers.placeholderPath) {
        this.imgUrl = this.uiHelpers.placeholderPath;
        this.uiHelpers.errorSnackbar(`Some images failed to load.`, 5000);
      }
    };
  }

  onFullImageLoaded(): void {
    this.fullWidth = this.fullSizeImage.nativeElement.naturalWidth;
    this.fullHeight = this.fullSizeImage.nativeElement.naturalHeight;
    this.fullImageLoaded = true;
    this.checkImagesLoaded();
  }

  private checkImagesLoaded(): void {
    this.calculateRatioAndOffset();

    if (this.thumbImageLoaded && this.fullImageLoaded) {
      this.calculateImageAndLensPosition();
      this.setIsReady(true);
    }
  }

  private setIsReady(value: boolean): void {
    this.isReady = value;
    this.imagesLoaded.emit(value);
  }

  onClick(event: MouseEvent): void {
    if (this.zoomingEnabled && this.zoomFrozen) {
      this.zoomFrozen = false;
      this.zoomOff();
    } else if (this.zoomingEnabled) {
      this.zoomFrozen = true;
      this.markForCheck();
    } else {
      this.zoomOn(event);
    }
  }

  onMouseLeave(): void {
    if (this.zoomingEnabled && !this.zoomFrozen) this.zoomOff();
  }

  onMouseMove(event: MouseEvent): void {
    if (this.zoomingEnabled && !this.zoomFrozen)
      this.calculateZoomPosition(event);
  }

  public zoomOn(event: MouseEvent): void {
    if (this.isReady) {
      this.zoomingEnabled = true;
      this.calculateRatioAndOffset();
      this.zoomDisplay = 'block';
      this.calculateZoomPosition(event);
    }
  }

  public zoomOff(): void {
    this.zoomingEnabled = false;
    this.zoomFrozen = false;
    this.zoomDisplay = 'none';
    this.markForCheck();
  }

  public markForCheck(): void {
    this.changeDetectorRef.markForCheck();
  }

  calculateRatioAndOffset(): void {
    // Handle disabled lens case
    if (!this.enableLens) {
      this.lensWidth = this.thumbWidth;
      this.lensHeight = this.thumbHeight;
      this.lensLeft = this.lensTop = 0;
    }

    if (this.fullImageLoaded) {
      // Calculate base ratio to maintain aspect ratio
      this.baseRatio = Math.max(
        this.thumbWidth / this.fullWidth,
        this.thumbHeight / this.fullHeight
      );

      // Don't allow zooming to smaller than thumbnail size
      this.minZoomRatio = Math.max(this.minZoomRatio || 0, this.baseRatio || 0);
      this.calculateRatio();
    }
  }

  /**
   * Calculates magnification ratio
   */
  calculateRatio(): void {
    this.magnifiedWidth = this.fullWidth * this.magnification;
    this.magnifiedHeight = this.fullHeight * this.magnification;
    this.xRatio = (this.magnifiedWidth - this.thumbWidth) / this.thumbWidth;
    this.yRatio = (this.magnifiedHeight - this.thumbHeight) / this.thumbHeight;
  }

  /**
   * Calculates zoom position based on mouse event coordinates
   */
  calculateZoomPosition(event: MouseEvent): void {
    // Ensure coordinates stay within image boundaries
    const newLeft = Math.max(Math.min(event.offsetX, this.thumbWidth), 0);
    const newTop = Math.max(Math.min(event.offsetY, this.thumbHeight), 0);

    this.latestMouseLeft = newLeft;
    this.latestMouseTop = newTop;

    // Emit position change
    this.zoomPosition.next({ x: newLeft, y: newTop });

    // Recalculate lens and image positions
    this.calculateImageAndLensPosition();

    // Trigger change detection
    this.markForCheck();
  }

  /**
   * Calculates lens and full image positions
   */
  calculateImageAndLensPosition(): void {
    let lensLeftMod = 0;
    let lensTopMod = 0;

    // Calculate lens position if enabled
    if (this.enableLens && this.latestMouseLeft > 0) {
      // Center lens on mouse position
      lensLeftMod = this.latestMouseLeft - this.lensWidth / 2;
      lensTopMod = this.latestMouseTop - this.lensHeight / 2;

      // Ensure lens stays within image boundaries
      lensLeftMod = Math.max(
        0,
        Math.min(lensLeftMod, this.thumbWidth - this.lensWidth)
      );
      lensTopMod = Math.max(
        0,
        Math.min(lensTopMod, this.thumbHeight - this.lensHeight)
      );

      this.lensLeft = lensLeftMod;
      this.lensTop = lensTopMod;
    }

    // Calculate full image position for zoom effect
    this.fullImageLeft = this.latestMouseLeft * -this.xRatio - lensLeftMod;
    this.fullImageTop = this.latestMouseTop * -this.yRatio - lensTopMod;
  }
}
